import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import MonthlyCalendar from "@/components/MonthlyCalendar";
import { TrendingUp, BookOpen, Dumbbell, BookMarked, Award, Calendar } from "lucide-react";

const ProgressPage = () => {
  const { language } = useContext(AuthContext);
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState({ totalLessons: 0, totalExercises: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [progressRes, lessonsRes, exercisesRes] = await Promise.all([
        axios.get(`${API}/progress`, { headers }),
        axios.get(`${API}/lessons`, { headers }),
        axios.get(`${API}/exercises`, { headers })
      ]);

      setProgress(progressRes.data);
      setStats({
        totalLessons: lessonsRes.data.length,
        totalExercises: exercisesRes.data.length
      });
    } catch (error) {
      console.error("Error fetching progress:", error);
      toast.error("Error al cargar progreso");
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const calculatePercentage = (completed, total) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">Cargando...</div>
        </div>
      </div>
    );
  }

  const lessonsCompleted = progress?.lessons_completed?.length || 0;
  const exercisesCompleted = progress?.exercises_completed?.length || 0;
  const journalEntries = progress?.total_journal_entries || 0;
  
  const lessonsPercentage = calculatePercentage(lessonsCompleted, stats.totalLessons);
  const exercisesPercentage = calculatePercentage(exercisesCompleted, stats.totalExercises);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div data-testid="progress-header" className="mb-12 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 glow-text">
            {t("Tu Progreso", "El Teu Progrés")}
          </h1>
          <p className="text-lg text-gray-300">
            {t("Visualiza tu evolución en el camino de automaestría", "Visualitza la teva evolució en el camí d'autoconeixement")}
          </p>
        </div>

        {/* Overview Cards */}
        <div data-testid="progress-overview" className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 fade-in" style={{animationDelay: '0.1s'}}>
          <div className="glass rounded-2xl p-6 border border-teal-400/20">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                <Award className="w-6 h-6 text-white" />
              </div>
              <TrendingUp className="w-6 h-6 text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-teal-200 mb-1">
              {lessonsCompleted + exercisesCompleted + journalEntries}
            </h3>
            <p className="text-sm text-gray-400">{t("Actividades Completadas", "Activitats Completades")}</p>
          </div>

          <div className="glass rounded-2xl p-6 border border-amber-400/20">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <BookMarked className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-amber-200 mb-1">{journalEntries}</h3>
            <p className="text-sm text-gray-400">{t("Entradas en Diario", "Entrades al Diari")}</p>
          </div>
        </div>

        {/* Monthly Activity Calendar */}
        <div className="mb-12 fade-in" style={{animationDelay: '0.15s'}}>
          <MonthlyCalendar />
        </div>

        {/* Detailed Progress */}
        <div data-testid="progress-details" className="space-y-6 fade-in" style={{animationDelay: '0.2s'}}>
          {/* Lessons Progress */}
          <div className="glass rounded-2xl p-6 border border-teal-400/10">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-6 h-6 text-teal-300" />
              <h3 className="text-xl font-semibold text-teal-200">
                {t("Lecciones", "Lliçons")}
              </h3>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300">
                {lessonsCompleted} {t("de", "de")} {stats.totalLessons} {t("completadas", "completades")}
              </span>
              <span className="text-teal-300 font-semibold">{lessonsPercentage}%</span>
            </div>
            <div className="w-full bg-black/30 rounded-full h-3 overflow-hidden">
              <div
                data-testid="lessons-progress-bar"
                className="h-full bg-gradient-to-r from-teal-400 to-teal-600 rounded-full transition-all duration-500"
                style={{ width: `${lessonsPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* Exercises Progress */}
          <div className="glass rounded-2xl p-6 border border-purple-400/10">
            <div className="flex items-center gap-3 mb-4">
              <Dumbbell className="w-6 h-6 text-purple-300" />
              <h3 className="text-xl font-semibold text-purple-200">
                {t("Ejercicios", "Exercicis")}
              </h3>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300">
                {exercisesCompleted} {t("de", "de")} {stats.totalExercises} {t("completados", "completats")}
              </span>
              <span className="text-purple-300 font-semibold">{exercisesPercentage}%</span>
            </div>
            <div className="w-full bg-black/30 rounded-full h-3 overflow-hidden">
              <div
                data-testid="exercises-progress-bar"
                className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full transition-all duration-500"
                style={{ width: `${exercisesPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* Journal Stats */}
          <div className="glass rounded-2xl p-6 border border-amber-400/10">
            <div className="flex items-center gap-3 mb-4">
              <BookMarked className="w-6 h-6 text-amber-300" />
              <h3 className="text-xl font-semibold gold-accent">
                {t("Diario Personal", "Diari Personal")}
              </h3>
            </div>
            <p className="text-gray-300">
              {t(
                `Has escrito ${journalEntries} ${journalEntries === 1 ? 'entrada' : 'entradas'} en tu diario personal.`,
                `Has escrit ${journalEntries} ${journalEntries === 1 ? 'entrada' : 'entrades'} al teu diari personal.`
              )}
            </p>
          </div>
        </div>

        {/* Motivational Message */}
        <div data-testid="motivational-message" className="mt-12 glass rounded-3xl p-8 text-center border border-teal-400/20 fade-in" style={{animationDelay: '0.3s'}}>
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-semibold mb-3 glow-text">
            {t("¡Sigue así!", "Continua així!")}
          </h3>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {t(
              "Cada paso en tu camino de automaestría es una victoria. Continúa explorando, reflexionando y creciendo con consciencia.",
              "Cada pas en el teu camí d'autoconeixement és una victòria. Continua explorant, reflexionant i creixent amb consciència."
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressPage;